import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PATH } from '@/router/PATH';
import { entryWindowForDate } from '../../../registersMeta';
import type {
    ChartAccount,
    DisposalPreview,
    FixedAsset,
    JournalEntry,
    LocaleCode,
    PlausibilityWarning,
} from '@/api/models';
import {
    useCommitDisposalMutation,
    useInvalidatePostedAssetEntry,
    usePreviewDisposalMutation,
} from '@/api/queries';
import { parseGelToTetri } from '@/pages/accounting/books/chartOfAccounts/utils';
import { todayIso } from '@/pages/accounting/statements/statementMeta';
import type { AccountOption } from '@/pages/accounting/journalEntry/journalEntryMeta';
import {
    computeGainOrLossTetri,
    disposalDirection,
    fixedAssetErrorMessage,
    type DisposalDirection,
} from '../../fixedAssetsMeta';

/** Default landing account for proceeds — the bank account. */
const DEFAULT_PROCEEDS_ACCOUNT_CODE = '1120';

type UseDisposeParams = {
    /** The asset being disposed; `null` closes the dialog. */
    asset: FixedAsset | null;
    accounts: ChartAccount[];
    locale: LocaleCode;
    onClose: () => void;
};

/**
 * The state the brief calls out and the API forces: *disposal recorded, entry
 * drafted, not yet posted*.
 *
 * A real user sits here. The terms are saved, a balanced draft exists in the
 * journal, and the asset is **still active** — because its position moves when
 * the entry posts, not when the terms are recorded.
 */
export type PendingDisposal = {
    entryId: string;
    entryNumber: string | null;
    /** Still `ACTIVE` — shown so the dialog can say so rather than imply it. */
    assetName: string;
};

export type DisposeModel = {
    disposalDate: string;
    proceeds: string;
    proceedsAccountId: string | null;
    accountOptions: AccountOption[];
    onDisposalDateChange: (value: string) => void;
    onProceedsChange: (value: string) => void;
    onProceedsAccountChange: (accountId: string | null) => void;
    /**
     * The figures on screen: **the server's once it has stated them**, and the
     * client-side arithmetic before that so the gain or loss stays live as the
     * user types. See {@link DisposeModel.serverPreview}.
     */
    netBookValueTetri: number;
    proceedsTetri: number;
    gainOrLossTetri: number;
    direction: DisposalDirection;
    /** True when proceeds are nil — the account picker is hidden and unsent. */
    isNilProceeds: boolean;
    proceedsErrorKey: string | null;
    dateErrorKey: string | null;
    isValid: boolean;
    isPreviewing: boolean;
    isCommitting: boolean;
    prepareError: string | null;
    /** Server advisories — non-blocking. `DISPOSAL_LOSS_DEBITS_REVENUE` lives here. */
    warnings: PlausibilityWarning[];
    /**
     * The **server's** figures, once a preview or commit has returned them.
     *
     * Null before then, and the dialog falls back to the live client-side
     * arithmetic so the gain or loss updates as the user types. Once this is
     * non-null it wins: it is what the server used to build the entry, and
     * confirming figures the server did not compute is how a user approves one
     * number and posts another — a depreciation run posting between the asset
     * read and the disposal is enough to make them differ.
     */
    serverPreview: DisposalPreview | null;
    /** True once a preview has run, so the figures on screen are the server's. */
    hasPreview: boolean;
    onPreview: () => Promise<void>;
    onConfirm: () => Promise<void>;
    /**
     * The footer's primary action: **preview first, record second**.
     *
     * A disposal is irreversible bookkeeping, and the server's figures are the
     * ones that matter — including any advisory it attaches. So the first press
     * asks the server what this books and shows the answer; only the second
     * records it. The client-side gain/loss above updates live as a running
     * estimate, but it is not what the user confirms against.
     */
    onPrimaryAction: () => Promise<void>;
    primaryLabelKey: string;
    /** The saved disposal draft open in the entry form; null when none is. */
    draft: JournalEntry | null;
    onDraftClose: () => void;
    onDraftSaved: (entry: JournalEntry) => void;
    /** Non-null while the disposal is recorded but its entry has not posted. */
    pending: PendingDisposal | null;
    onPendingDismiss: () => void;
    /** Dismiss the pending notice and open its entry in the Journal tab. */
    onOpenPendingEntry: () => void;
};

/**
 * The disposal flow.
 *
 * Three steps, and the third is the one the API's shape forces into the open:
 *
 * 1. **Preview** — the server computes cost out, accumulated depreciation out,
 *    proceeds in and the gain or loss, and returns any advisories. Writes nothing.
 * 2. **Confirm** — records the terms and persists a balanced DRAFT entry. The
 *    asset is **not** disposed by this: it comes back `ACTIVE`, with
 *    `disposalDate`, `disposalProceeds` and `disposalEntryId` all still null.
 * 3. **Post** — done by a person in the Journal Entry form. *This* is what
 *    disposes the asset and moves the ledger.
 *
 * Between 2 and 3 sits {@link PendingDisposal}, and it is deliberately visible.
 * Optimistically flipping the badge to Disposed would claim a position the
 * ledger does not hold, and the register would disagree with the books until
 * someone noticed. So the dialog says what is actually true — the asset is still
 * active, the disposal is pending its entry, and the entry is one click away.
 *
 * The measured constraint behind that: **the asset carries no trace of a pending
 * disposal**, and `entries[]` on the detail is posted-only. So this state cannot
 * be recovered from the register after a reload — it is held here, for the
 * session in which it was created, and the register-level notice covers the rest.
 */
export const useDispose = ({
    asset,
    accounts,
    locale,
    onClose,
}: UseDisposeParams): DisposeModel => {
    const { t } = useTranslation('pages');
    const preview = usePreviewDisposalMutation();
    const commit = useCommitDisposalMutation();
    const invalidatePosted = useInvalidatePostedAssetEntry();

    const defaultAccountId = React.useMemo(
        () =>
            accounts.find(
                (account) => account.code === DEFAULT_PROCEEDS_ACCOUNT_CODE,
            )?.id ?? null,
        [accounts],
    );

    const navigate = useNavigate();
    const [disposalDate, setDisposalDate] = React.useState(todayIso);
    const [proceeds, setProceeds] = React.useState('');
    const [proceedsAccountId, setProceedsAccountId] = React.useState<
        string | null
    >(defaultAccountId);
    const [prepareError, setPrepareError] = React.useState<string | null>(null);
    const [draft, setDraft] = React.useState<JournalEntry | null>(null);
    const [warnings, setWarnings] = React.useState<PlausibilityWarning[]>([]);
    const [serverPreview, setServerPreview] =
        React.useState<DisposalPreview | null>(null);
    const [hasPreview, setHasPreview] = React.useState(false);
    const [pending, setPending] = React.useState<PendingDisposal | null>(null);

    // A fresh asset means a fresh dialog — nothing leaks between rows.
    React.useEffect(() => {
        setDisposalDate(todayIso());
        setProceeds('');
        setProceedsAccountId(defaultAccountId);
        setPrepareError(null);
        setWarnings([]);
        setServerPreview(null);
        setHasPreview(false);
        setPending(null);
    }, [asset?.id, defaultAccountId]);

    const accountOptions = React.useMemo<AccountOption[]>(
        () =>
            accounts
                .filter((account) => account.isPostable && !account.archived)
                .map((account) => ({
                    id: account.id,
                    code: account.code,
                    name: account.name,
                    searchText: `${account.code} ${account.name}`,
                }))
                .sort((a, b) => a.code.localeCompare(b.code)),
        [accounts],
    );

    const parsed = parseGelToTetri(proceeds);
    // An empty field is a legitimate zero — scrapping an asset for nothing —
    // and so is an unparseable one, which surfaces as a field error instead.
    const proceedsTetri = parsed.ok ? parsed.tetri : 0;
    const proceedsErrorKey =
        parsed.ok || parsed.reason === 'empty'
            ? null
            : parsed.reason === 'sub-tetri'
              ? 'fixedAssets.form.errors.subTetri'
              : parsed.reason === 'negative'
                ? 'fixedAssets.form.errors.negative'
                : 'fixedAssets.form.errors.invalidAmount';

    // Nil proceeds is a scrapping: there is no counter-account to name, and the
    // server refuses one (`PROCEEDS_ACCOUNT_NOT_ALLOWED`). Requiring the picker
    // here would make scrapping unreachable through the UI.
    const isNilProceeds = proceedsTetri === 0;

    const netBookValueTetri = asset?.netBookValueTetri ?? 0;
    const gainOrLossTetri = computeGainOrLossTetri(
        netBookValueTetri,
        proceedsTetri,
    );

    const dateErrorKey =
        asset && disposalDate !== '' && disposalDate < asset.acquisitionDate
            ? 'fixedAssets.dispose.errors.beforeAcquisition'
            : disposalDate === ''
              ? 'fixedAssets.dispose.errors.dateRequired'
              : null;

    const isValid =
        proceedsErrorKey === null &&
        dateErrorKey === null &&
        (isNilProceeds || proceedsAccountId !== null);

    const body = React.useCallback(
        () => ({
            disposalDate,
            proceeds: proceeds.trim() === '' ? '0' : proceeds.trim(),
            // Sent only when there are proceeds to land — see `isNilProceeds`.
            proceedsAccountId: isNilProceeds ? null : proceedsAccountId,
            locale,
        }),
        [disposalDate, proceeds, isNilProceeds, proceedsAccountId, locale],
    );

    // Editing any term invalidates the preview — the figures and the advisory
    // belong to the terms that produced them, and showing them against changed
    // ones would be attributing the server's answer to a different question.
    React.useEffect(() => {
        setHasPreview(false);
        setWarnings([]);
    }, [disposalDate, proceeds, proceedsAccountId]);

    // A preview belongs to the terms it was computed from. Changing the date,
    // the proceeds or the account drops it rather than leaving the server's
    // figures on screen next to inputs that no longer produced them.
    React.useEffect(() => {
        setServerPreview(null);
        setHasPreview(false);
        setWarnings([]);
    }, [disposalDate, proceeds, proceedsAccountId]);

    const onPreview = React.useCallback(async () => {
        if (!asset || !isValid) return;
        setPrepareError(null);
        const result = await preview.mutateAsync({
            assetId: asset.id,
            body: body(),
        });
        if (!result.success) {
            setPrepareError(fixedAssetErrorMessage(result.error, t));
            return;
        }
        setWarnings(result.response.warnings);
        // The authoritative figures — what the server will book, not what the
        // possibly-stale asset read implies.
        setServerPreview(result.response.preview);
        setHasPreview(true);
    }, [asset, isValid, preview, body, t]);

    /**
     * Record the terms and persist the draft, then open it.
     *
     * The preview's draft cannot be opened — it has no id, because nothing behind
     * it exists yet. The commit is what creates a real entry.
     */
    const onConfirm = React.useCallback(async () => {
        if (!asset || !isValid) return;
        setPrepareError(null);
        const result = await commit.mutateAsync({
            assetId: asset.id,
            body: body(),
        });
        if (!result.success) {
            setPrepareError(fixedAssetErrorMessage(result.error, t));
            return;
        }
        // `alreadyExisted` is not an error — this asset already had a disposal
        // draft and the server handed back the same one rather than a second.
        setWarnings(result.response.warnings);
        // The commit returns its own preview: the figures actually behind the
        // draft the user is about to review.
        setServerPreview(result.response.preview);
        setHasPreview(true);
        setDraft(result.response.draft);
    }, [asset, isValid, commit, body, t]);

    /**
     * The entry form closed. Whether the user saved, posted or simply dismissed
     * it, **the draft still exists** — the commit persisted it, and closing a
     * form is not a withdrawal.
     *
     * So the only question is whether it *posted*. If it did, the asset is
     * disposed and the flow is finished. If it did not, the disposal is pending
     * its entry, and saying so is the whole point of this state.
     */
    const settle = React.useCallback(
        (entry: JournalEntry | null) => {
            setDraft(null);
            if (entry?.status === 'POSTED') {
                // Posting is what disposes the asset and moves the ledger, so
                // the register, the balances and the tie-out all have to refetch
                // — the journal's post mutation invalidates only the journal.
                invalidatePosted(entry);
                onClose();
                return;
            }
            const openDraft = entry ?? draft;
            if (openDraft && asset) {
                setPending({
                    entryId: openDraft.id,
                    entryNumber: openDraft.entryNumber ?? null,
                    assetName: asset.name[locale] ?? asset.name.en ?? asset.id,
                });
                return;
            }
            onClose();
        },
        [asset, draft, locale, invalidatePosted, onClose],
    );

    const onDraftClose = React.useCallback(() => settle(null), [settle]);
    const onDraftSaved = React.useCallback(
        (entry: JournalEntry) => settle(entry),
        [settle],
    );

    const onPendingDismiss = React.useCallback(() => {
        setPending(null);
        onClose();
    }, [onClose]);

    /**
     * Dismiss the pending notice and open its entry in the Journal **tab**,
     * scoped to the disposal's own date — that tab reads its window from the
     * Books shell and cannot widen it for a deep link.
     *
     * Lives here rather than inline in the dialog for the repo's render/logic
     * split, and because the guard matters: `disposalDate` is a form string, so
     * an unreadable one has to drop the link rather than put `Invalid Date` in
     * the URL. Dismissing still happens either way — the notice has been read.
     */
    const onOpenPendingEntry = React.useCallback(() => {
        const entryId = pending?.entryId;
        const window = entryWindowForDate(disposalDate);
        onPendingDismiss();
        if (entryId && window) {
            navigate(
                PATH.ACCOUNTING.BOOKS.BIND_JOURNAL_FOR_ENTRY(entryId, window),
            );
        }
    }, [pending, disposalDate, onPendingDismiss, navigate]);

    const onPrimaryAction = React.useCallback(
        () => (hasPreview ? onConfirm() : onPreview()),
        [hasPreview, onConfirm, onPreview],
    );

    // The server's figures win the moment it has stated them; before that the
    // client-side arithmetic keeps the gain or loss live as the user types.
    // Resolved here rather than in the view so the view stays render-only and
    // there is exactly one place that decides which number is authoritative.
    const shownNetBookValueTetri =
        serverPreview?.netBookValueTetri ?? netBookValueTetri;
    const shownProceedsTetri = serverPreview?.proceedsTetri ?? proceedsTetri;
    const shownGainOrLossTetri =
        serverPreview?.gainOrLossTetri ?? gainOrLossTetri;

    return {
        disposalDate,
        proceeds,
        proceedsAccountId,
        accountOptions,
        onDisposalDateChange: setDisposalDate,
        onProceedsChange: setProceeds,
        onProceedsAccountChange: setProceedsAccountId,
        netBookValueTetri: shownNetBookValueTetri,
        proceedsTetri: shownProceedsTetri,
        gainOrLossTetri: shownGainOrLossTetri,
        direction: disposalDirection(shownGainOrLossTetri),
        isNilProceeds,
        proceedsErrorKey,
        dateErrorKey,
        isValid,
        isPreviewing: preview.isPending,
        isCommitting: commit.isPending,
        prepareError,
        warnings,
        serverPreview,
        hasPreview,
        onPreview,
        onConfirm,
        onPrimaryAction,
        primaryLabelKey: hasPreview
            ? 'fixedAssets.actions.recordDisposal'
            : 'fixedAssets.actions.previewDisposal',
        draft,
        onDraftClose,
        onDraftSaved,
        pending,
        onPendingDismiss,
        onOpenPendingEntry,
    };
};
